import type {
  ExclusionScope,
  HouseholdSummary,
  CookingGuidance,
  LibraryMeal,
  Me,
  Member,
  ShoppingList,
  TodayView,
  Weather,
  WeekView,
  Language,
  Household,
  HouseholdAccess,
  HouseholdDevice,
  BatchOptions,
  MealChoices,
  MealDraft,
  PickupDay,
  PickupTemplate,
} from './types';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep the generic message */
    }
    throw new ApiError(message, response.status);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });

const withLang = (path: string, language: Language): string =>
  `${path}${path.includes('?') ? '&' : '?'}lang=${language}`;

export const api = {
  me: () => request<Me>('/me'),
  signOut: () => post<{ ok: true }>('/auth/sign-out'),
  requestMagicLink: (email: string) =>
    post<{ sent: boolean; devLink?: string }>('/auth/magic-link', { email }),
  redeem: (token: string) => post<{ householdId: string }>('/auth/redeem', { token }),
  claimDevice: (token: string) => post<{ ok: true }>('/auth/claim-device', { token }),

  /** Who can sign in, and invitations still outstanding. */
  access: () => request<HouseholdAccess>('/access'),
  invite: (email: string) =>
    post<{ sent: boolean; devLink?: string; access: HouseholdAccess }>('/invites', { email }),
  cancelInvite: (email: string) =>
    request<HouseholdAccess>('/invites', { method: 'DELETE', body: JSON.stringify({ email }) }),
  setMealPlanner: (id: string, isMealPlanner: boolean) =>
    post<HouseholdAccess>(`/access/${id}/planner`, { isMealPlanner }),
  removeAccess: (id: string) => request<HouseholdAccess>(`/access/${id}`, { method: 'DELETE' }),

  /** The kitchen tablet: a browser that stays signed in for good. */
  devices: () => request<HouseholdDevice[]>('/devices'),
  createDevice: (name: string) =>
    post<{ id: string; name: string; token: string; claimUrl: string }>('/devices', { name }),
  revokeDevice: (id: string) => request<{ ok: true }>(`/devices/${id}`, { method: 'DELETE' }),
  /** Start a household. An address that already has one gets a sign-in link. */
  signUp: (householdName: string, email: string) =>
    post<{ householdId?: string; needsSetup?: boolean; existing?: boolean }>('/auth/sign-up', {
      householdName,
      email,
    }),
  /** First run: who eats here, and when the shop happens. */
  completeSetup: (input: {
    householdName?: string;
    location?: string;
    members: Array<{ name: string; member_type: 'adult' | 'child' | 'aupair' }>;
    shoppingDays: Array<{ weekday: number; shop_type: string; required?: boolean }>;
  }) => post<{ ok: true }>('/household/setup', input),

  household: () =>
    request<{ household: Household; members: Member[]; shoppingTemplates: unknown[] }>('/household'),
  updateHousehold: (patch: Partial<Household>) =>
    request<{ ok: true }>('/household', { method: 'PATCH', body: JSON.stringify(patch) }),
  addMember: (member: Partial<Member>) => post<{ id: string }>('/members', member),
  updateMember: (id: string, patch: Partial<Member>) =>
    request<{ ok: true }>(`/members/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  removeMember: (id: string) => request<{ ok: true }>(`/members/${id}`, { method: 'DELETE' }),

  week: (date: string, language: Language) => request<WeekView>(withLang(`/week?date=${date}`, language)),
  setSlots: (weekStart: string, slots: unknown[], language: Language) =>
    post<WeekView>(withLang('/week/slots', language), { weekStart, slots }),
  generate: (date: string, language: Language) =>
    post<WeekView>(withLang(`/week/generate?date=${date}`, language)),
  optimise: (date: string, language: Language) =>
    post<WeekView>(withLang(`/week/optimise?date=${date}`, language)),
  reroll: (date: string, assignmentKey: string, language: Language) =>
    post<WeekView>(withLang(`/week/reroll?date=${date}`, language), { assignmentKey }),
  lock: (date: string, plannedMealId: string, locked: boolean, language: Language) =>
    post<WeekView>(withLang(`/week/lock?date=${date}`, language), { plannedMealId, locked }),
  setMeal: (date: string, assignmentKey: string, mealId: string, language: Language) =>
    post<WeekView>(withLang(`/week/set-meal?date=${date}`, language), { assignmentKey, mealId }),
  /** Drag one meal onto another: the two change places (§34). */
  swap: (date: string, fromKey: string, toKey: string, language: Language) =>
    post<WeekView>(withLang(`/week/swap?date=${date}`, language), { fromKey, toKey }),
  /** What each person will not eat — a hard filter, not a preference. */
  /**
   * Where this household's week starts and which one to open on.
   *
   * The week runs from one main shop to the next, so its boundary is a
   * household fact rather than a calendar one — the web cannot work it out and
   * used to assume Monday for everybody.
   */
  weekTarget: (today: string) =>
    request<{
      today: string;
      thisWeek: string;
      planningWeek: string;
      weekStartsOn: number;
    }>(`/week/target?today=${today}`),
  /** What the store cupboard is believed to hold (§20). */
  pantry: (language: Language) =>
    request<
      Array<{
        ingredientId: string;
        name: string;
        quantity: number;
        unit: string;
        source: 'inferred' | 'manual';
        addedOn: string;
      }>
    >(withLang('/pantry', language)),
  setPantryItem: (ingredientId: string, quantity: number, unit: string) =>
    request<{ ok: true }>(`/pantry/${ingredientId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity, unit }),
    }),
  removePantryItem: (ingredientId: string) =>
    request<{ ok: true }>(`/pantry/${ingredientId}`, { method: 'DELETE' }),
  /** Operator's view of every household on the service (§7). */
  adminHouseholds: () =>
    request<{ households: HouseholdSummary[] }>('/admin/households'),
  adminDeleteHousehold: (id: string) =>
    request<{ ok: true }>(`/admin/households/${id}`, { method: 'DELETE' }),
  /** Whether the signed-in person runs the service. */
  adminWhoami: () => request<{ operator: boolean }>('/admin/whoami'),
  exclusions: () =>
    request<Array<{ memberId: string; ingredientId: string; scope: ExclusionScope }>>(
      '/exclusions',
    ),
  setExclusion: (
    memberId: string,
    ingredientId: string,
    excluded: boolean,
    scope: ExclusionScope = 'never',
  ) =>
    request<{ ok: true }>(`/members/${memberId}/ingredients/${ingredientId}`, {
      method: 'PUT',
      body: JSON.stringify({
        hard_exclusion: excluded,
        exclusion_scope: scope,
        preference_weight: 0,
      }),
    }),

  /** The ingredient library, for adding something to a list by hand. */
  ingredients: () =>
    request<
      Array<{ id: string; name: string; category: string; default_unit: string; cupboard_staple: boolean }>
    >('/ingredients'),

  /** Which days the household normally shops, and where. */
  saveShoppingTemplates: (templates: unknown[]) =>
    request<{ ok: true }>('/shopping-templates', {
      method: 'PUT',
      body: JSON.stringify({ templates }),
    }),

  /** Cook once, eat twice: where else this week could be fed from one cook. */
  batchOptions: (date: string, assignmentKey: string, language: Language) =>
    request<BatchOptions>(
      withLang(
        `/week/batch-options?date=${date}&assignmentKey=${encodeURIComponent(assignmentKey)}`,
        language,
      ),
    ),
  linkBatch: (
    date: string,
    sourceKey: string,
    targetKey: string,
    becomesMealId: string,
    language: Language,
  ) =>
    post<WeekView & { batchStorage: 'fridge' | 'freezer' }>(
      withLang(`/week/batch?date=${date}`, language),
      { sourceKey, targetKey, becomesMealId },
    ),
  unlinkBatch: (date: string, plannedMealId: string, language: Language) =>
    request<WeekView>(withLang(`/week/batch?date=${date}`, language), {
      method: 'DELETE',
      body: JSON.stringify({ plannedMealId }),
    }),

  /** What could go in this dish, favourites first. */
  mealChoices: (date: string, assignmentKey: string, language: Language) =>
    request<MealChoices>(
      withLang(`/week/choices?date=${date}&assignmentKey=${encodeURIComponent(assignmentKey)}`, language),
    ),

  /** Who is making this meal — anyone in the household, or nobody yet. */
  setCook: (date: string, plannedMealId: string, memberId: string | null, language: Language) =>
    post<WeekView>(withLang(`/week/cook?date=${date}`, language), { plannedMealId, memberId }),

  markCooked: (plannedMealId: string) => post<{ ok: true }>('/week/cooked', { plannedMealId }),

  today: (language: Language) => request<TodayView>(withLang('/today', language)),
  weather: (language: Language) => request<Weather>(withLang('/weather', language)),

  shopping: (date: string, language: Language) =>
    request<{ lists: ShoppingList[] }>(withLang(`/shopping?date=${date}`, language)),
  generateShopping: (date: string, language: Language) =>
    post<{
      lists: ShoppingList[];
      emailed: boolean;
      whatsapp: { sent: number } | { error: string } | null;
    }>(withLang(`/shopping/generate?date=${date}`, language)),
  sendShoppingToWhatsApp: (date: string, language: Language) =>
    post<{ sent: number }>(withLang(`/shopping/whatsapp?date=${date}`, language)),
  checkItem: (id: string, checked: boolean) => post<{ ok: true }>(`/shopping/item/${id}/check`, { checked }),
  addItem: (tripId: string, ingredientId: string, quantity: number) =>
    post<{ ok: true }>('/shopping/item', { tripId, ingredientId, quantity }),
  removeItem: (id: string) => request<{ ok: true }>(`/shopping/item/${id}`, { method: 'DELETE' }),
  completeTrip: (id: string, completed: boolean) =>
    post<{ weekStatus: string }>(`/shopping/trip/${id}/complete`, { completed }),
  /** A one-off shop for this week only — the standing pattern is untouched. */
  addTrip: (date: string, tripDate: string, shopType: string, required = false) =>
    post<ShoppingList[]>(`/shopping/trip?date=${date}`, { date: tripDate, shopType, required }),
  moveTrip: (id: string, tripDate: string) =>
    request<ShoppingList[]>(`/shopping/trip/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ date: tripDate }),
    }),
  removeTrip: (id: string) =>
    request<ShoppingList[]>(`/shopping/trip/${id}`, { method: 'DELETE' }),

  enableTrip: (id: string, enabled: boolean) => post<{ ok: true }>(`/shopping/trip/${id}/enable`, { enabled }),
  emailShopping: (date: string, tripIds: string[], language: Language) =>
    post<{ delivered: boolean; transport: string; recipients: string[] }>(
      withLang(`/shopping/email?date=${date}`, language),
      { tripIds },
    ),
  printUrl: (date: string, tripIds: string[], language: Language) =>
    `/api/shopping/print?date=${date}&lang=${language}${tripIds.length ? `&trips=${tripIds.join(',')}` : ''}`,

  exportUrl: () => '/api/household/export',
  importBackup: (backup: unknown) =>
    post<{ mealPreferences: number; members: number; skippedMeals: string[] }>(
      '/household/import',
      backup,
    ),

  meals: (filter: string, q: string) =>
    request<LibraryMeal[]>(`/meals?filter=${filter}&q=${encodeURIComponent(q)}`),
  favourite: (id: string, favourite: boolean, memberId?: string | null) =>
    post<{ ok: true }>(`/meals/${id}/favourite`, { favourite, memberId: memberId ?? null }),
  setRegular: (id: string, isRegular: boolean) => post<{ ok: true }>(`/meals/${id}/regular`, { isRegular }),
  /** Turn a dish name into a working meal, ingredients and all. */
  draftMeal: (name: string, language: Language, course?: string) =>
    post<MealDraft>(withLang('/meals/draft', language), { name, course }),
  createMeal: (meal: unknown) => post<{ id: string }>('/meals', meal),
  updateMeal: (id: string, meal: unknown) =>
    request<{ id: string }>(`/meals/${id}`, { method: 'PUT', body: JSON.stringify(meal) }),
  deleteMeal: (id: string) => request<{ ok: true }>(`/meals/${id}`, { method: 'DELETE' }),

  block: (id: string, blocked: boolean) => post<{ ok: true }>(`/meals/${id}/block`, { blocked }),

  pickups: (date: string) => request<PickupDay>(`/pickups?date=${date}`),
  setPickup: (input: {
    date: string;
    memberId: string;
    pickedUpBy?: string | null;
    time?: string | null;
    place?: string | null;
    notRequired?: boolean;
  }) => post<PickupDay>('/pickups', input),
  clearPickup: (date: string, memberId: string) =>
    request<PickupDay>('/pickups', { method: 'DELETE', body: JSON.stringify({ date, memberId }) }),

  /** The standing weekly pattern — set once, used until it is changed. */
  pickupTemplates: () => request<PickupTemplate[]>('/pickup-templates'),
  savePickupTemplates: (templates: PickupTemplate[]) =>
    request<{ ok: true }>('/pickup-templates', {
      method: 'PUT',
      body: JSON.stringify({ templates }),
    }),
  clearPickupOverrides: (from: string) =>
    request<{ cleared: number }>('/pickups/overrides', {
      method: 'DELETE',
      body: JSON.stringify({ from }),
    }),

  cook: (plannedMealId: string, language: Language, refresh = false) =>
    request<CookingGuidance>(withLang(`/cook/${plannedMealId}${refresh ? '?refresh=1' : ''}`, language)),
  ask: (plannedMealId: string, question: string, language: Language) =>
    post<{ answer: string; generatedBy: string }>(withLang(`/cook/${plannedMealId}/ask`, language), {
      question,
    }),
};
